/**
 * Report Description — edit modal.
 * Label is read-only (display only); Description editable; Query readonly + Copy; Name hidden.
 */

import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Copy } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/features/application/styles/modals.module.css'
import { showSuccess } from '@/lib/utils/notifications'
import { cn } from '@/lib/utils/helpers'
import {
  reportDescriptionEditSchema,
  type ReportDescriptionFormValues,
} from '../types/schemas'
import type { ReportDescriptionEditModalProps } from '../types'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

export function ReportDescriptionEditModal({
  isOpen,
  report,
  submitting,
  onClose,
  onSubmit,
}: ReportDescriptionEditModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm<ReportDescriptionFormValues>({
    resolver: zodResolver(reportDescriptionEditSchema),
    mode: 'onChange',
    defaultValues: {
      report_name: '',
      report_label: '',
      report_description: '',
      report_query: '',
    },
  })

  useEffect(() => {
    if (!isOpen) return
    reset({
      report_name: report?.report_name ?? '',
      report_label: report?.report_label ?? '',
      report_description: report?.report_description ?? '',
      report_query: report?.report_query ?? '',
    })
  }, [isOpen, report, reset])

  const internalSubmit = (values: ReportDescriptionFormValues) => {
    onSubmit(
      {
        report_name: values.report_name,
        report_label: values.report_label,
        report_description: values.report_description,
        report_query: values.report_query,
      },
      report?.report_name || undefined,
    )
  }

  // Angular's `copyText()` reports success unconditionally — no empty check.
  const copyQuery = async () => {
    try {
      await navigator.clipboard.writeText(getValues('report_query') ?? '')
    } catch {
      // ignored, matching Angular's fire-and-forget execCommand('copy')
    }
    showSuccess('Query Copied Successfully!')
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={report ? 'Edit Report Description' : 'Add Report Description'}
      onClose={onClose}
      maxWidth="max-w-2xl"
      bodyScrollOnly
      panelDataTestId="report-description-edit-modal-panel"
      footer={
        <div
          className="flex flex-wrap items-center justify-center gap-3"
          data-testid="report-description-edit-modal-footer"
        >
          <button
            type="submit"
            form="report-description-edit-form"
            disabled={submitting || !isValid}
            className={cn(primaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="report-description-edit-submit"
          >
            {submitting ? 'Saving...' : 'Submit'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className={cn(secondaryEntityActionButtonClass, 'min-w-[120px]')}
            data-testid="report-description-edit-cancel"
          >
            Cancel
          </button>
        </div>
      }
    >
      <form
        id="report-description-edit-form"
        onSubmit={handleSubmit(internalSubmit)}
        className="space-y-4 p-2"
        noValidate
      >
        <input type="hidden" {...register('report_name')} />

        <div>
          <label
            htmlFor="report-label"
            className="mb-1 block text-sm font-semibold text-black"
          >
            Label
          </label>
          <input
            id="report-label"
            type="text"
            readOnly
            tabIndex={-1}
            className="w-full cursor-default rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-slate-700 focus:outline-none"
            {...register('report_label')}
          />
        </div>

        <div>
          <label
            htmlFor="report-description"
            className="mb-1 block text-sm font-semibold text-black"
          >
            Description
          </label>
          <textarea
            id="report-description"
            rows={3}
            className={cn(
              'w-full rounded-lg border px-3 py-2 focus:border-[#1597ff] focus:outline-none focus:ring-2 focus:ring-[#1597ff]',
              errors.report_description ? 'border-red-500' : 'border-slate-300',
            )}
            aria-invalid={Boolean(errors.report_description)}
            {...register('report_description')}
          />
          {errors.report_description && (
            <p className={modalStyles.formFieldError} role="alert">
              {errors.report_description.message}
            </p>
          )}
        </div>

        <div>
          <div className="mb-1 flex items-center justify-between">
            <label htmlFor="report-query" className="block text-sm font-semibold text-black">
              Query
            </label>
            <button
              type="button"
              onClick={copyQuery}
              className="inline-flex items-center gap-1 text-xs font-medium text-[#1597ff] hover:text-[#0d7acc]"
            >
              <Copy className="h-3 w-3" /> Copy
            </button>
          </div>
          <textarea
            id="report-query"
            rows={5}
            readOnly
            className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 font-mono text-xs focus:outline-none"
            {...register('report_query')}
          />
        </div>
      </form>
    </OnScreenModal>
  )
}
